import { Injectable, signal } from '@angular/core';
import {
  ClientJourneyState,
  JourneyChannel,
  JourneyDetailPayload,
  JourneyDirection,
  JourneyEvent,
  JourneyEventType,
  JourneySeverity,
  JourneySourceType,
} from './client-journey.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers — same djb2 pattern as other state services
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(id: string, key: string, options: readonly T[]): T {
  const h = djb2(`${id}:${key}`);
  return options[h % options.length];
}

function seedInt(id: string, key: string, min: number, max: number): number {
  const h = djb2(`${id}:${key}`);
  return min + (h % (max - min + 1));
}

function seedBool(id: string, key: string): boolean {
  return djb2(`${id}:${key}`) % 2 === 0;
}

function isoDateTime(daysAgo: number, hourOffset = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hourOffset % 24, 0, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Lookup tables
// ---------------------------------------------------------------------------

const FA_NAMES = ['Sarah Chen', 'Marcus Webb', 'Priya Nair', 'David Torres'] as const;
const CLIENT_NAMES = ['Alex Johnson', 'Jordan Smith', 'Taylor Williams', 'Morgan Davis'] as const;
const TAGS_POOL = ['Benefits', 'Savings', 'Debt', 'Escalation', 'Retirement', 'Insurance', 'Housing', 'Tax'] as const;

const EVENT_TYPE_POOL: JourneyEventType[] = [
  'email', 'sms', 'call', 'chat',
  'opportunity_created', 'opportunity_updated', 'opportunity_committed', 'opportunity_completed',
  'action_created', 'action_updated', 'action_completed', 'action_blocked',
  'outcome_detected', 'escalation', 'handoff', 'financial_milestone', 'system',
];

const CHANNEL_BY_TYPE: Record<JourneyEventType, JourneyChannel> = {
  email:                    'email',
  sms:                      'sms',
  call:                     'phone',
  chat:                     'chat',
  opportunity_created:      'crm',
  opportunity_updated:      'crm',
  opportunity_committed:    'crm',
  opportunity_completed:    'crm',
  opportunity_withdrawn:    'crm',
  action_created:           'crm',
  action_updated:           'crm',
  action_completed:         'crm',
  action_blocked:           'crm',
  action_withdrawn:         'crm',
  outcome_detected:         'system',
  escalation:               'crm',
  handoff:                  'crm',
  financial_milestone:      'system',
  system:                   'system',
};

const SOURCE_BY_TYPE: Record<JourneyEventType, JourneySourceType> = {
  email:                    'fa',
  sms:                      'fa',
  call:                     'fa',
  chat:                     'aiAgent',
  opportunity_created:      'aiAgent',
  opportunity_updated:      'fa',
  opportunity_committed:    'fa',
  opportunity_completed:    'fa',
  opportunity_withdrawn:    'fa',
  action_created:           'aiAgent',
  action_updated:           'fa',
  action_completed:         'fa',
  action_blocked:           'system',
  action_withdrawn:         'fa',
  outcome_detected:         'system',
  escalation:               'aiAgent',
  handoff:                  'aiAgent',
  financial_milestone:      'system',
  system:                   'system',
};

interface EventTemplate {
  title: string;
  summary: string;
  severity?: JourneySeverity;
  direction?: JourneyDirection;
  transcript?: string;
  actorChain?: string[];
  confidence?: number;
  goalAligned?: boolean;
  escalationReason?: string;
  metricSnippet?: string;
  notes?: string;
}

const TEMPLATES: Record<JourneyEventType, EventTemplate[]> = {
  email: [
    {
      title: 'Follow-up on retirement rollover options',
      summary: 'Sent a detailed breakdown of IRA rollover options with projected tax impact for the current year.',
      direction: 'outbound',
      transcript: 'Hi Alex,\n\nFollowing up on our call last week — I\'ve attached a summary of the three IRA rollover options we discussed. Option A (direct rollover) avoids the 20% withholding and keeps your full balance working for you immediately.\n\nPlease review and let me know if you have questions.\n\nBest,\nSarah',
      actorChain: ['FA', 'Client'],
    },
    {
      title: 'Client inquiry: credit card debt consolidation',
      summary: 'Client emailed asking about debt consolidation options after receiving a high-interest credit card statement.',
      direction: 'inbound',
      transcript: 'Hi Sarah,\n\nI got my credit card statement and the interest rate jumped to 24.99%. I\'m struggling to keep up. Is there a way to consolidate this into something more manageable?\n\nThanks,\nAlex',
      actorChain: ['Client', 'FA'],
    },
    {
      title: 'Benefits enrollment deadline reminder',
      summary: 'Sent reminder about open enrollment deadline and recommended reviewing HSA contribution limits.',
      direction: 'outbound',
      transcript: 'Hi Jordan,\n\nJust a reminder that your employer\'s open enrollment period closes in 5 days. Based on your current health usage, I recommend maximizing your HSA contribution this year — it\'s a triple tax advantage.\n\nLet me know if you\'d like to review your options.\n\nSarah',
      actorChain: ['FA', 'Client'],
    },
  ],
  sms: [
    {
      title: 'Appointment confirmation: financial review',
      summary: 'Confirmed upcoming quarterly financial review appointment via SMS.',
      direction: 'outbound',
      actorChain: ['FA', 'Client'],
    },
    {
      title: 'Client: urgent question about savings withdrawal',
      summary: 'Client texted asking about early withdrawal penalties on their 401(k) due to a family emergency.',
      direction: 'inbound',
      severity: 'warn',
      actorChain: ['Client', 'FA'],
    },
    {
      title: 'Document upload reminder',
      summary: 'Sent reminder to upload last two pay stubs for loan pre-qualification.',
      direction: 'outbound',
      actorChain: ['FA', 'Client'],
    },
  ],
  call: [
    {
      title: 'Outbound call: quarterly portfolio review',
      summary: '32-minute call reviewing Q3 portfolio performance, rebalancing strategy, and upcoming tax-loss harvesting opportunity.',
      direction: 'outbound',
      actorChain: ['FA', 'Client'],
      notes: 'Client expressed concern about market volatility. Agreed to schedule a follow-up in 30 days.',
    },
    {
      title: 'Inbound call: missed — escalation triggered',
      summary: 'Client called during off-hours. AI agent handled initial inquiry and flagged for FA follow-up.',
      direction: 'inbound',
      severity: 'warn',
      actorChain: ['Client', 'AI Agent', 'FA Queue'],
    },
    {
      title: 'Call: debt hardship discussion',
      summary: 'Client initiated call regarding inability to make minimum payments. Discussed hardship options and payment deferral.',
      direction: 'inbound',
      severity: 'danger',
      actorChain: ['Client', 'FA'],
      notes: 'Client is 2 months behind on auto loan. Referred to hardship program.',
    },
  ],
  chat: [
    {
      title: 'AI chat: savings goal check-in',
      summary: 'AI agent conducted automated savings goal check-in. Client confirmed on track. No FA action required.',
      actorChain: ['AI Agent', 'Client'],
      confidence: 0.91,
      goalAligned: true,
    },
    {
      title: 'AI chat: debt payoff calculator session',
      summary: 'Client used AI-assisted debt payoff calculator. AI recommended avalanche method. Opportunity flagged for FA review.',
      actorChain: ['Client', 'AI Agent'],
      confidence: 0.87,
      goalAligned: true,
    },
    {
      title: 'Chat escalated to FA: housing question',
      summary: 'Client asked about mortgage refinancing options. AI agent escalated to FA after detecting complexity threshold.',
      actorChain: ['Client', 'AI Agent', 'FA Handoff'],
      severity: 'warn',
      confidence: 0.62,
      goalAligned: false,
    },
  ],
  opportunity_created: [
    {
      title: 'Opportunity: IRA rollover from employer 401(k)',
      summary: 'BrightAI detected rollover opportunity from previous employer 401(k). Estimated benefit: $4,200 in fee savings over 10 years.',
      actorChain: ['AI Agent'],
      confidence: 0.88,
      goalAligned: true,
      metricSnippet: 'Est. fee savings: $4,200 over 10 years',
    },
    {
      title: 'Opportunity: life insurance coverage gap identified',
      summary: 'Coverage gap detected during annual review. Current policy $250K; recommended $500K based on income and dependents.',
      actorChain: ['AI Agent', 'FA Review'],
      confidence: 0.79,
      metricSnippet: 'Coverage gap: $250,000',
    },
  ],
  opportunity_updated: [
    {
      title: 'Opportunity updated: 529 plan for college savings',
      summary: 'FA updated opportunity status after client meeting. Client expressed strong interest; moving to committed.',
      actorChain: ['FA'],
    },
    {
      title: 'Opportunity updated: debt consolidation loan',
      summary: 'Updated with new lender quotes. Best rate: 9.5% APR vs current 22.4% average.',
      actorChain: ['FA'],
      metricSnippet: 'Rate reduction: 22.4% → 9.5% APR',
    },
  ],
  opportunity_committed: [
    {
      title: 'Opportunity committed: IRA rollover',
      summary: 'Client confirmed intent to proceed with IRA rollover. Paperwork initiated.',
      actorChain: ['FA', 'Client'],
      goalAligned: true,
    },
  ],
  opportunity_completed: [
    {
      title: 'Opportunity completed: HSA contribution maximized',
      summary: 'Client successfully maximized HSA contribution for the year. Tax savings realized.',
      actorChain: ['FA', 'Client'],
      goalAligned: true,
      metricSnippet: 'Tax savings: ~$1,100',
    },
  ],
  opportunity_withdrawn: [
    {
      title: 'Opportunity withdrawn: auto refinance',
      summary: 'Client decided not to proceed with auto refinance due to prepayment penalty.',
      actorChain: ['Client', 'FA'],
    },
  ],
  action_created: [
    {
      title: 'Action created: send estate planning checklist',
      summary: 'AI agent created action for FA to send updated estate planning checklist following beneficiary review.',
      actorChain: ['AI Agent'],
      confidence: 0.94,
    },
    {
      title: 'Action created: schedule portfolio review call',
      summary: 'Quarterly portfolio review call scheduled for next month.',
      actorChain: ['FA'],
    },
  ],
  action_updated: [
    {
      title: 'Action updated: debt paydown plan',
      summary: 'FA updated action with revised paydown timeline after client income change.',
      actorChain: ['FA'],
    },
  ],
  action_completed: [
    {
      title: 'Action completed: beneficiary forms submitted',
      summary: 'FA confirmed beneficiary designation forms submitted and processed.',
      actorChain: ['FA'],
      goalAligned: true,
    },
    {
      title: 'Action completed: credit report review',
      summary: 'Completed review of client credit report. Identified 2 errors for dispute.',
      actorChain: ['FA', 'Client'],
      metricSnippet: '2 errors identified for dispute',
    },
  ],
  action_blocked: [
    {
      title: 'Action blocked: loan application on hold',
      summary: 'Loan application paused pending updated income documentation from client.',
      severity: 'warn',
      actorChain: ['System'],
    },
  ],
  action_withdrawn: [
    {
      title: 'Action withdrawn: insurance policy switch',
      summary: 'Client decided to keep existing policy after reviewing premium comparison.',
      actorChain: ['Client', 'FA'],
    },
  ],
  outcome_detected: [
    {
      title: 'Outcome: savings rate improved 8%',
      summary: 'System detected client savings rate increased from 6% to 14% over 90 days following debt paydown plan.',
      severity: 'success',
      metricSnippet: 'Savings rate: 6% → 14% (+8 pts)',
      goalAligned: true,
    },
    {
      title: 'Outcome: credit score improved +42 points',
      summary: 'Credit score increased 42 points after utilization dropped below 30% threshold.',
      severity: 'success',
      metricSnippet: 'Credit score: +42 pts',
      goalAligned: true,
    },
  ],
  escalation: [
    {
      title: 'Escalation: client distress signal detected',
      summary: 'AI agent detected high-distress language during chat session. Immediate FA follow-up flagged.',
      severity: 'danger',
      actorChain: ['AI Agent', 'FA Escalation Queue'],
      escalationReason: 'Client expressed inability to pay rent and mentioned considering early 401(k) withdrawal.',
      confidence: 0.95,
    },
    {
      title: 'Escalation: missed payment risk',
      summary: 'Three consecutive missed minimum payments detected. Escalated to FA for intervention.',
      severity: 'danger',
      actorChain: ['System', 'AI Agent', 'FA'],
      escalationReason: 'Client has missed 3 consecutive minimum payments on credit card.',
      metricSnippet: '3 missed payments detected',
    },
  ],
  handoff: [
    {
      title: 'AI handoff to FA: complex tax question',
      summary: 'AI agent transferred session to FA after client asked about Roth conversion tax implications beyond AI confidence threshold.',
      actorChain: ['Client', 'AI Agent', 'FA'],
      confidence: 0.58,
      notes: 'Client needs guidance on Roth conversion ladder strategy.',
    },
    {
      title: 'Handoff: after-hours escalation resolved',
      summary: 'FA picked up after-hours escalation. Client concern addressed; follow-up call scheduled.',
      actorChain: ['AI Agent', 'FA'],
    },
  ],
  financial_milestone: [
    {
      title: 'Milestone: emergency fund target reached',
      summary: 'Client reached 3-month emergency fund target after 8 months of consistent contributions.',
      severity: 'success',
      metricSnippet: 'Emergency fund: 3 months coverage achieved',
      goalAligned: true,
    },
    {
      title: 'Milestone: debt-free on medical bills',
      summary: 'Final medical debt payment processed. Client is now debt-free on all medical obligations.',
      severity: 'success',
      metricSnippet: 'Medical debt: $0 (fully paid)',
      goalAligned: true,
    },
    {
      title: 'Milestone: credit utilization below 30%',
      summary: 'Credit utilization dropped below 30% threshold for the first time in 18 months.',
      severity: 'success',
      metricSnippet: 'Utilization: 28% (below 30% threshold)',
    },
  ],
  system: [
    {
      title: 'System: client profile data refreshed',
      summary: 'Automated data refresh completed. Account balances and credit data updated.',
      actorChain: ['System'],
    },
    {
      title: 'System: annual review reminder triggered',
      summary: 'Annual financial review reminder sent to FA queue. Review due within 30 days.',
      actorChain: ['System', 'FA Queue'],
    },
  ],
};

// ---------------------------------------------------------------------------
// Seeding function
// ---------------------------------------------------------------------------

function seedJourney(id: string): ClientJourneyState {
  const count = seedInt(id, 'event_count', 15, 25);
  const events: JourneyEvent[] = [];

  // Spread events across 180 days
  const daySpread = Math.floor(180 / count);

  for (let i = 0; i < count; i++) {
    const eventType = seedPick<JourneyEventType>(id, `evt_type_${i}`, EVENT_TYPE_POOL);
    const templates = TEMPLATES[eventType];
    const template = seedPick(id, `evt_tmpl_${i}`, templates);

    const channel = CHANNEL_BY_TYPE[eventType];
    const sourceType = SOURCE_BY_TYPE[eventType];
    const sourceName = sourceType === 'fa'
      ? seedPick(id, `evt_fa_${i}`, FA_NAMES)
      : sourceType === 'client'
      ? seedPick(id, `evt_client_${i}`, CLIENT_NAMES)
      : sourceType === 'aiAgent'
      ? 'BrightAI Agent'
      : 'System';

    const daysAgo = (i * daySpread) + seedInt(id, `evt_day_offset_${i}`, 0, daySpread - 1);
    const hourOffset = seedInt(id, `evt_hour_${i}`, 8, 18);
    const occurredAt = isoDateTime(daysAgo, hourOffset);

    const tagCount = seedInt(id, `evt_tag_count_${i}`, 1, 3);
    const tags: string[] = [];
    for (let t = 0; t < tagCount; t++) {
      const tag = seedPick(id, `evt_tag_${i}_${t}`, TAGS_POOL);
      if (!tags.includes(tag)) tags.push(tag);
    }

    const detailPayload: JourneyDetailPayload = {
      transcript: template.transcript,
      actorChain: template.actorChain,
      confidence: template.confidence,
      goalAligned: template.goalAligned,
      escalationReason: template.escalationReason,
      metricSnippet: template.metricSnippet,
      notes: template.notes,
      linkedOpportunityTitles: eventType.startsWith('opportunity') ? [template.title] : undefined,
      linkedActionTitles: eventType.startsWith('action') ? [template.title] : undefined,
    };

    const event: JourneyEvent = {
      id: `journey-${id.slice(0, 8)}-${i}`,
      eventType,
      channel,
      title: template.title,
      summary: template.summary,
      occurredAt,
      createdAt: occurredAt,
      updatedAt: occurredAt,
      sourceType,
      sourceName,
      direction: template.direction,
      tags,
      severity: template.severity,
      linkedOpportunityIds: eventType.startsWith('opportunity') ? [`opp-${i}`] : [],
      linkedActionIds: eventType.startsWith('action') ? [`act-${i}`] : [],
      linkedOutcomeIds: eventType === 'outcome_detected' ? [`out-${i}`] : [],
      metadata: {
        eventTypeKey: eventType,
        channel,
        sourceType,
        seedIndex: i,
      },
      detailPayload,
    };

    events.push(event);
  }

  // Sort newest first
  events.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

  return { events };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class ClientJourneyStateService {
  private readonly _store = signal<Record<string, ClientJourneyState>>({});

  readonly storeSnapshot = this._store.asReadonly();

  ensureState(sessionId: string): void {
    if (this._store()[sessionId]) return;
    this._store.update(s => ({ ...s, [sessionId]: seedJourney(sessionId) }));
  }
}
